import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

import { cn } from '@/lib/utils';

export function Pagination({ className, ...props }: ComponentPropsWithoutRef<'nav'>) {
  return <nav aria-label="pagination" className={cn('flex items-center', className)} {...props} />;
}

export function PaginationContent({ className, ...props }: ComponentPropsWithoutRef<'ul'>) {
  return <ul className={cn('flex items-center gap-1', className)} {...props} />;
}

export function PaginationItem({ className, ...props }: ComponentPropsWithoutRef<'li'>) {
  return <li className={className} {...props} />;
}

type PaginationButtonProps<T extends ElementType = 'button'> = {
  as?: T;
  isActive?: boolean;
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children'>;

export function PaginationButton<T extends ElementType = 'button'>({
  as,
  isActive,
  className,
  children,
  ...props
}: PaginationButtonProps<T>) {
  const Component = as ?? 'button';

  return (
    <Component
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex size-8 items-center justify-center rounded-md border text-sm transition-colors',
        isActive
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-background text-foreground hover:bg-muted',
        'disabled:pointer-events-none disabled:opacity-40',
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

export function PaginationEllipsis({ className, ...props }: ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-8 items-center justify-center text-sm text-muted-foreground',
        className,
      )}
      {...props}
    >
      ...
    </span>
  );
}
